<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import DropdownShell from "@comp-lib/components/DropdownShell.vue";
import TenantCard from "@/components/TenantCard.vue";
import { useUserStore } from "@/stores/userStore";
import { useToasterStore } from "@comp-lib/stores";
import { useModalStore } from "@comp-lib/stores/modalStore";
import { removeAccessToTenant } from "@/services/tenantManagementService";
import { HttpError } from "@/services/HttpError";
import { storeToRefs } from "pinia";
import type { MobiusTenant } from "@/types";

const router = useRouter();
const route = useRoute();

const userStore = useUserStore();
const { tenants, currentTenant, hasAdminAccess, storeState } = storeToRefs(userStore);
const { setCurrentTenant, refreshToken } = userStore;

const toasterStore = useToasterStore();
const { triggerNotif } = toasterStore;

const modalStore = useModalStore();
const { openModal } = modalStore;

const isUnlinking = ref(false);

const isOnAdminRoute = computed(() => route.path.startsWith("/admin"));
const showAdminConsoleOption = computed(() => hasAdminAccess.value && tenants.value.length > 0);

const triggerLabel = computed(() => {
  if (isOnAdminRoute.value && hasAdminAccess.value) return "Admin Console";
  if (currentTenant.value) return currentTenant.value.name;
  return "Select tenant";
});

const shouldShowDropdown = computed(() => {
  if (storeState.value === "switching") return false;
  const optionCount = tenants.value.length + (showAdminConsoleOption.value ? 1 : 0);
  return optionCount > 1;
});

function handleTenantSelect(tenant: MobiusTenant) {
  if (currentTenant.value && currentTenant.value.id === tenant.id && !isOnAdminRoute.value) return;
  const redirected = setCurrentTenant(tenant, "/");
  if (!redirected) router.push("/");
}

function handleAdminConsoleSelect() {
  if (isOnAdminRoute.value) return;
  router.push("/admin");
}

function confirmUnlinkTenant(tenantId: number, tenantName: string) {
  openModal({
    title: "Unlink tenant",
    textContent: `Are you sure you want to unlink from "${tenantName}"? You will lose access to this tenant. Note that you can always regain access by logging into the tenant again.`,
    confirmMethod: () => handleUnlinkTenant(tenantId, tenantName),
    confirmText: "Yes, unlink",
    destructive: true,
  });
}

async function handleUnlinkTenant(tenantId: number, tenantName: string) {
  const wasCurrentTenant = currentTenant.value?.id === tenantId;
  isUnlinking.value = true;

  try {
    await removeAccessToTenant(tenantId);
    await refreshToken();

    triggerNotif({
      type: "success",
      title: "Tenant unlinked",
      bodyText: `Successfully unlinked from ${tenantName}.`
    });

    if (wasCurrentTenant) {
      currentTenant.value = null;
      router.push("/admin");
    }
  } catch (error) {
    console.error("Failed to unlink tenant:", error);
    triggerNotif({
      type: "error",
      title: "Failed to unlink tenant",
      bodyText: HttpError.getErrorMessage(error, "An unexpected error occurred.")
    });
  } finally {
    isUnlinking.value = false;
  }
}

function isCurrent(tenant: MobiusTenant): boolean {
  if (isOnAdminRoute.value) return false;
  return currentTenant.value?.id === tenant.id;
}
</script>

<template>
  <div v-if="shouldShowDropdown" class="tenant-switch-wrapper">
    <DropdownShell :title="triggerLabel" position="right">
      <div class="tenant-panel">
        <!-- Admin console option (admins only) -->
        <TenantCard
          v-if="showAdminConsoleOption"
          admin
          :current="isOnAdminRoute"
          @select="handleAdminConsoleSelect"
        />

        <!-- Tenant list -->
        <TenantCard
          v-for="tenant in tenants"
          :key="tenant.id"
          :tenant="tenant"
          :current="isCurrent(tenant)"
          :showUnlink="hasAdminAccess"
          :unlinkDisabled="isUnlinking"
          @select="handleTenantSelect(tenant)"
          @unlink="confirmUnlinkTenant(tenant.id, tenant.name)"
        />
      </div>
    </DropdownShell>
  </div>
</template>

<style lang="scss" scoped>
.tenant-switch-wrapper {
  flex: 1 1 150px;
  min-width: 0;
}
.tenant-panel {
  background-color: white;
  border-radius: map.get(v.$border-radius, "md");
  box-shadow: map.get(v.$shadow, "lg");
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 400px;
  overflow-y: auto;
  min-width: 300px;
  width: max-content;
}
</style>
